/**
 * Utility service to dispatch application lead notifications and payment success reports to:
 * - Admin (admin@formsadda.com)
 * - Applicant User Email
 */

const ADMIN_EMAIL = 'admin@formsadda.com';

/**
 * Dispatches Lead Form Submission Emails to Admin & User
 */
export async function sendApplicationEmails(leadData) {
  const userEmail = leadData.email || leadData.userEmail || '';
  const fullName = leadData.fullName || leadData.name || 'Applicant';
  const phone = leadData.phone || leadData.mobile || '';
  const course = leadData.course || leadData.university || 'Christ University Online Programme';
  const city = leadData.city || 'Not specified';
  const amount = leadData.amount || '₹500';
  const timestamp = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });

  const adminMessage = `
--- NEW ADMISSION LEAD RECEIVED ---
Applicant Name: ${fullName}
Mobile Number : +91 ${phone}
Email Address : ${userEmail}
Program       : ${course}
City/Location : ${city}
Token Fee     : ${amount}
Submitted At  : ${timestamp}
---
FormsADDA Admin Notification Engine (admin@formsadda.com)
  `.trim();

  const userReplyMessage = `
Dear ${fullName},

Thank you for registering your application interest for ${course} at CHRIST (Deemed to be University) Online Programmes via FormsADDA.

We have successfully received your details (+91 ${phone}). An official academic admission counsellor will reach out to you shortly to assist with document verification and enrollment.

Best regards,
Admission Support Team
FormsADDA (https://formsadda.com)
  `.trim();

  const payload = {
    type: 'LEAD_FORM',
    admin_email: ADMIN_EMAIL,
    user_email: userEmail,
    subject: `[New Lead] ${fullName} - ${course}`,
    applicant_name: fullName,
    applicant_phone: `+91 ${phone}`,
    course_name: course,
    city: city,
    fee_amount: amount,
    date: timestamp,
    admin_body: adminMessage,
    user_reply_body: userReplyMessage
  };

  console.log(`[Lead Email Dispatch] Admin (${ADMIN_EMAIL}) & User (${userEmail}):`, payload);

  try {
    const params = new URLSearchParams({
      admin_email: ADMIN_EMAIL,
      user_email: userEmail,
      name: fullName,
      phone: phone,
      course: course,
      city: city,
      amount: amount
    });
    
    // Beacon GET execution
    const img = new Image();
    img.src = `https://formsadda.com/api/lead_email.php?${params.toString()}`;
    
    // Formspree API POST backup
    fetch('https://formspree.io/f/xbjnqkyv', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      mode: 'no-cors'
    }).catch(() => {});
  } catch (err) {
    console.log('[Lead Email Dispatch] Exception:', err);
  }

  return { success: true, adminEmail: ADMIN_EMAIL, userEmail };
}

/**
 * Dispatches Official Payment Success Emails (with Unique Application Number & Invoice Receipt)
 * to User Email and Admin Email (admin@formsadda.com)
 */
export async function sendPaymentSuccessEmail(paymentData) {
  const userEmail = paymentData.email || paymentData.userEmail || '';
  const fullName = paymentData.fullName || paymentData.name || 'Applicant';
  const phone = paymentData.phone || paymentData.mobile || '';
  const course = paymentData.course || 'Christ University Online Programme';
  const appNumber = paymentData.appNumber || 'Christ-University-27001';
  const txnId = paymentData.txnId || `PAYU_${Date.now()}`;
  const amount = paymentData.amount || '₹500';
  const city = paymentData.city || 'N/A';
  const timestamp = paymentData.formattedDate || new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });

  const adminPaymentMessage = `
=== CONFIRMED ADMISSION APPLICATION & PAYMENT RECEIPT ===
Unique Application No : ${appNumber}
PayU Transaction Ref  : ${txnId}
Payment Status        : SUCCESS / VERIFIED
Amount Paid           : ${amount}
Applicant Name        : ${fullName}
Mobile Number         : +91 ${phone}
Email Address         : ${userEmail}
Program Selected      : ${course}
City/State            : ${city}
Date & Time           : ${timestamp}
=========================================================
Admin Portal Notice: Full registration fee verified via PayU Merchant Account.
FormsADDA Admin Engine (admin@formsadda.com)
  `.trim();

  const userPaymentMessage = `
Dear ${fullName},

CONGRATULATIONS! Your application for CHRIST (Deemed to be University) Online Programmes has been successfully submitted and verified.

OFFICIAL REGISTRATION DETAILS:
--------------------------------------------------
Application Number   : ${appNumber}
Payment Transaction  : ${txnId}
Fee Paid             : ${amount} (PayU Verified)
Program              : ${course}
Applicant Name       : ${fullName}
Mobile Number        : +91 ${phone}
Date & Time          : ${timestamp}
--------------------------------------------------

Your official application invoice has been generated. An academic counselor will contact you shortly with next steps for document submission.

Warm Regards,
Admissions Office
CHRIST (Deemed to be University) Online Programmes
FormsADDA Portal (https://formsadda.com)
  `.trim();

  const payload = {
    type: 'PAYMENT_SUCCESS',
    app_number: appNumber,
    admin_email: ADMIN_EMAIL,
    user_email: userEmail,
    subject: `[CONFIRMED APPLICATION ${appNumber}] ${fullName} - ${course}`,
    applicant_name: fullName,
    applicant_phone: `+91 ${phone}`,
    course_name: course,
    txn_id: txnId,
    fee_amount: amount,
    date: timestamp,
    admin_body: adminPaymentMessage,
    user_reply_body: userPaymentMessage
  };

  console.log(`[Payment Success Dispatch] Sent to Admin (${ADMIN_EMAIL}) & User (${userEmail}):`, payload);

  try {
    const params = new URLSearchParams({
      type: 'payment_success',
      app_no: appNumber,
      admin_email: ADMIN_EMAIL,
      user_email: userEmail,
      name: fullName,
      phone: phone,
      course: course,
      txn_id: txnId,
      amount: amount
    });
    
    // Beacon GET execution
    const img = new Image();
    img.src = `https://formsadda.com/api/payment_email.php?${params.toString()}`;
    
    // Formspree API POST backup
    fetch('https://formspree.io/f/xbjnqkyv', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      mode: 'no-cors'
    }).catch(() => {});
  } catch (err) {
    console.log('[Payment Email Dispatch] Exception:', err);
  }

  return { success: true, appNumber, adminEmail: ADMIN_EMAIL, userEmail };
}
